<template>
  <div class="ProductB2CLayout">
    <!-- * * * * * * * PRODUCT IMAGE AND BUY BOX -->
    <div
      class="desk:grid grid-cols-[80px_minmax(0,_1fr)_repeat(10,_minmax(0,_1fr))] gap-x-12
             mb-48 desk:mb-80 desk:pt-16 desk:layout-container"
    >
      <!-- spalt 1 -->
      <div class="hidden desk:block col-span-1">
        <div id="ProductImageThumbnails" class=" relative h-full max-h-[468px]" />
      </div>

      <!-- spalt 2 -->
      <div class="mobOnly:w-full mobOnly:mb-16 desk:col-span-6 text-left">
        <ProductImage
          :key="`productImage${activeVariant.partNo}`"
          :product-item="productItem"
          :current-image-list="currentImageList"
          :active-variant="activeVariant"
        />
      </div>

      <!-- spalt 3 -->
      <div class="mobOnly:w-full desk:col-span-5 desk:pl-28">
        <div class="mobOnly:px-12 ">
          <ProductBreadcrumbs v-if="uiStore.isMobile" :product-item="productItem" />

          <!-- * * * * * * name & category -->
          <B2CNameAndCategory
            :product-item="productItem"
            :active-variant="activeVariant"
          />

          <!-- * * * * * * price -->
          <PricePublic
            v-if="!globalContent.disablePricingAndPurchase"
            :product-item="productItem"
            :active-variant="activeVariant"
          />

          <!-- * * * * * * samples button only for samples only products -->
          <BuyBoxSamplesOnly :product-item="productItem" :active-variant="activeVariant" />
        </div>

        <!-- * * * * * * variant selector -->
        <VariantSelector
          :product-item="productItem"
          :active-variant="activeVariant"
          design="standard"
          @update-active-variant="(variant) => updateActiveVariant(variant)"
        />
        <div class="mobOnly:px-12">
          <!-- * * * * * *  buy samples -->
          <BuyBox
            v-if="activeVariant"
            :key="activeVariant.partNo"
            :active-variant="activeVariant"
            :product-item="productItem"
            design="standard"
          />

          <!-- * * * * find retailer -->
          <!--          <FindRetailerQuickBox />-->
        </div>
      </div>
    </div>

    <!-- * * * * * * * Rest of the content -->
    <div class="layout-container">
      <!-- * * * * Quick Jump * * * * -->
      <B2CAnchorLinks v-if="!isLimitedContent && props.productItem.categories.some(s => s.code === 'Floors')" />
      <ProductPersonality
        v-if="!isLimitedContent && props.productItem.categories.some(s => s.code === 'Floors')"
        :active-variant="activeVariant"
        :product-item="productItem"
      />

      <!-- * * * * product accesories
      <ProductAccessories
        :active-variant="activeVariant"
        :product-item="productItem"
        design="b2c"
      />
      -->

      <!-- * * * * * * cevoid wrapper -->
      <div>
        <div
          id="cevoid-container"
          data-gallery="g-CBV2BrIevDI43spZ30aBJ"
          data-product="auto"
        />
      </div>
    </div>

    <!-- * * * * mood gallery -->
    <MoodGallery
      v-if="!isLimitedContent"
      :active-variant="activeVariant"
      :product-item="productItem"
    />

    <!-- * * * * generic content -->
    <SpaceHandler v-if="genericContent && genericContent[0] && !isLimitedContent" :blok="genericContent[0]">
      <component :is="genericContent[0].component" :blok="genericContent[0]" />
    </SpaceHandler>

    <!-- * * * * product facts/files -->
    <div class="bg-bg-secondary py-48 desk:py-96">
      <div class="desktopMaxW-sm mobOnly:px-12 mx-auto">
        <a id="pdpShowParametrics" />
        <div class="type-headline-2xl mb-32">{{ $t('productPage.info.about') }}</div>
        <GenericAccordion
          :headline="$t('productPage.productFacts')"
          size="xl"
        >
          <ShowParametrics
            :active-variant="activeVariant"
            :product-item="productItem"
            class="mb-32"
          />
        </GenericAccordion>
        <ProductFiles
          :active-variant="activeVariant"
          :product-item="productItem"
          design="b2cLight"
        />
      </div>
    </div>

    <!-- * * * * generic content -->
    <SpaceHandler
      v-if="genericContent && genericContent[1] && !isLimitedContent"
      :blok="genericContent[1]"
    >
      <component :is="genericContent[1].component" :blok="genericContent[1]" />
    </SpaceHandler>

    <FloorPlannerPromo
      v-if="!isLimitedContent"
      :active-variant="activeVariant"
      :product-item="productItem"
    />

    <!-- * * * * generic content -->
    <SpaceHandler
      v-if="genericContent && genericContent[2] && !isLimitedContent"
      :blok="genericContent[2]"
    >
      <component :is="genericContent[2].component" :blok="genericContent[2]" />
    </SpaceHandler>

    <!-- * * * * related products -->

    <div
      class="desk:layout-container desk:mt-64"
      :class="{ 'mb-32 desk:mt-64': isLimitedContent }"
    >
      <RelatedProductsList :product-item="productItem" />
    </div>

    <ProductArticles
      v-if="!isLimitedContent"
      :product-item="productItem"
    />

    <!-- the rest of generic content -->
    <template v-if="genericContent && genericContent.length > 3 && !isLimitedContent">
      <SpaceHandler
        v-for="generic in genericContent.slice(3)"
        :key="generic._uid"
        :blok="generic"
      >
        <component :is="generic.component" :blok="generic" />
      </SpaceHandler>
    </template>
  </div>
</template>

<script lang="ts" setup>
import VariantSelector from '~/components/product-page/VariantSelector.vue';
import ProductImage from '~/components/product-page/ProductImage.vue';
import { ProductModel } from '~/models/product';
import { ProductVariantModel } from '~/models/productVariant';
import { NorceMediaSetCodes } from '~/constants/norceCodes';
import ShowParametrics from '~/components/product-page/ShowParametrics.vue';
import GenericAccordion from '~/components/generic/GenericAccordion.vue';
import ProductFiles from '~/components/product-page/ProductFiles.vue';
import ProductBreadcrumbs from '~/components/product-page/ProductBreadcrumbs.vue';
import { useUiStore } from '~/store/ui';
import B2CNameAndCategory from '~/components/product-page/B2CNameAndCategory.vue';
import PricePublic from '~/components/product-page/PricePublic.vue';
import BuyBoxSamplesOnly from '~/components/product-page/BuyBoxSamplesOnly.vue';
import { useGlobalContentStore } from '~/store/globalContent';
import useLinkReplacer from '~/composeables/useLinkReplacer';
import SpaceHandler from '~/components/body/SpaceHandler.vue';
import ProductAccessories from '~/components/product-page/ProductAccessories.vue';
import MoodGallery from '~/components/product-page/MoodGallery.vue';
import B2CAnchorLinks from '~/components/product-page/B2CAnchorLinks.vue';
import ProductPersonality from '~/components/product-page/ProductPersonality.vue';
import FloorPlannerPromo from '~/components/product-page/FloorPlannerPromo.vue';
import RelatedProductsList from '~/components/product-page/RelatedProductsList.vue';
import ProductArticles from '~/components/product-page/ProductArticles.vue';
import FindRetailerQuickBox from '~/components/product-page/FindRetailerQuickBox.vue';
import BuyBox from '~/components/product-page/BuyBox.vue';

const uiStore = useUiStore();
const storyblokApi = useStoryblokApi();
const globalContent = useGlobalContentStore();
const { replaceLinksInStoryBlokResponse } = useLinkReplacer();

const props = defineProps<{
  productItem: ProductModel,
  activeVariant: ProductVariantModel,
  currentImageList: NorceMediaSetCodes,
}>();
const emit = defineEmits<{
  (e: 'updateActiveVariant', obj: ProductVariantModel): void;
}>();

const updateActiveVariant = async(variant: ProductVariantModel) => {
  emit('updateActiveVariant', variant);
};

const genericContent = ref<any>(null);

const loadGenericContent = async() => {
  // Note that we dont use getConsumerTypeSettingsPath, this is b2c only
  const res = await storyblokApi.get(`cdn/stories/${globalContent.domainMap?.storyblokBasePath}/sitesettings/b2c-pdp-generic-content`, {
    version: useRuntimeConfig().public.storyblokStoriesVersion as 'draft' | 'published',
    resolve_links: 'url',
  }).catch(() => null);
  if (res) {
    genericContent.value = replaceLinksInStoryBlokResponse(res.data.story.content.body);
  }
};

const isLimitedContent = props.productItem.categories.some(s => s.code === 'Maintenance');

onMounted(()=> loadGenericContent());

</script>
